/**
 * DraftLorPage
 *
 * Standalone page for the legacy LOR drafting experience.
 * This page renders the old ClaimLorStep component with transcript
 * pane, evidence panel, and fax wizard functionality.
 */
import { Check } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { DraftClaimLorStep } from '@/components/claims-filing/lor/DraftClaimLorStep';
import { getLatestCoverageTaskByCanonicalType } from '@/components/claims-filing/premises/premisesFlow';
import { Button } from '@/components/ui/button';
import {
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import { useSendFaxFromBlobMutation } from '@/services/api/faxService';
import type { Task, TaskStatus } from '@/services/types/client-intake-types';
import type { AdjusterDetail } from '@/services/types/coverage-types';
import type { LoRScalarChoice } from '@/services/types/letters-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';
import {
  buildPreferredContactChoice,
  extractContactChannels,
} from '@/services/utils/coverageHandles';

interface FinalizeDetails {
  policyNumber: string;
  claimNumber: string;
  adjusterName: string;
  adjusterEmail: string;
  adjusterPhone: string;
  adjusterFax: string;
  preferredContactInstructions?: string;
  adjusterContactChoice: LoRScalarChoice | null;
}

type RouteParams = {
  matter_id: string;
  coverage_id: string;
};

export const DraftLorPage: React.FC<{ hideHeader?: boolean }> = ({
  hideHeader = false,
}) => {
  const { matter_id: matterId, coverage_id: coverageId } =
    useParams<RouteParams>();
  const [, setActiveFieldKey] = useState<string | null>(null);
  const [activeFaxTransmissions] = useState<Map<string, string>>(new Map());
  const [sendFaxFromBlob, { isLoading: isSendingFax }] =
    useSendFaxFromBlobMutation();

  // Task completion wiring
  const { data: tasksResponse } = useGetTasksQuery(
    {
      matter: matterId ?? '',
      limit: 200,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();

  const sendLorTask = useMemo(() => {
    const tasks = (tasksResponse?.results || []) as Task[];
    return getLatestCoverageTaskByCanonicalType(
      tasks,
      coverageId ?? '',
      CanonicalTaskType.SEND_LOR,
    );
  }, [tasksResponse, coverageId]);

  const isTaskCompleted =
    sendLorTask?.status?.canonical_status === CanonicalTaskStatus.COMPLETED;

  const completedStatusId = useMemo(() => {
    return (
      (taskStatuses as TaskStatus[]).find(
        (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
      )?.id ?? null
    );
  }, [taskStatuses]);

  const handleMarkComplete = useCallback(async () => {
    if (!sendLorTask || !completedStatusId || !matterId) return;
    try {
      await updateTask({
        matterId,
        taskId: sendLorTask.id,
        task: {
          status_id: completedStatusId,
          completed_at: new Date().toISOString(),
        },
      }).unwrap();
      toast.success('Task marked as complete');
    } catch {
      toast.error('Failed to mark task as complete');
    }
  }, [completedStatusId, matterId, sendLorTask, updateTask]);

  // Initialize finalize details directly from coverage data.
  // Previously used useCoverageLoRFields (lor-context endpoint); now derives
  // scalar defaults from the standard coverage query.
  const { data: coverageData } = useGetCoverageQuery(
    { matterId: matterId ?? '', coverageId: coverageId ?? '' },
    { skip: !matterId || !coverageId },
  );

  // Build initial finalize details from coverage data
  const buildInitialDetails = (): FinalizeDetails => {
    if (!coverageData) {
      return {
        policyNumber: '',
        claimNumber: '',
        adjusterName: '',
        adjusterEmail: '',
        adjusterPhone: '',
        adjusterFax: '',
        preferredContactInstructions: '',
        adjusterContactChoice: null,
      };
    }

    const primaryAdj = (coverageData.adjusters as AdjusterDetail[] | null)?.[0];
    const adjName = primaryAdj?.name ?? '';
    const handles = Array.isArray(primaryAdj?.handles) ? primaryAdj.handles : [];
    const { email: adjEmail, phone: adjPhone, fax: adjFax } =
      extractContactChannels(handles);
    const contactChoice = buildPreferredContactChoice(handles, coverageId ?? '');

    const policy = coverageData.policy_identifiers?.[0]?.identifier_value ?? '';
    const claim = coverageData.claims?.[0]?.claim_identifier ?? '';

    return {
      policyNumber: policy,
      claimNumber: claim,
      adjusterName: adjName,
      adjusterEmail: adjEmail,
      adjusterPhone: adjPhone,
      adjusterFax: adjFax,
      preferredContactInstructions: '',
      adjusterContactChoice: contactChoice,
    };
  };

  const [finalizeDetails, setFinalizeDetails] =
    useState<FinalizeDetails>(buildInitialDetails);
  const [hasUnsavedFinalizeChanges, setHasUnsavedFinalizeChanges] =
    useState(false);

  // Reset prefill guard when coverageId changes so new coverage data triggers prefill.
  const prefilledRef = useRef<boolean>(false);
  const prevCoverageIdRef = useRef<string | undefined>(coverageId);
  useEffect(() => {
    if (prevCoverageIdRef.current !== coverageId) {
      prefilledRef.current = false;
      prevCoverageIdRef.current = coverageId;
      setFinalizeDetails(buildInitialDetails());
      setHasUnsavedFinalizeChanges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverageId]);

  // Re-populate finalize details once coverage data loads (useState initializer
  // only runs on first render when coverageData is still undefined).
  // Guard prevents coverage refetches from overwriting user-cleared fields.
  useEffect(() => {
    if (prefilledRef.current) return;
    if (!coverageData) return;
    setFinalizeDetails((prev) => {
      // Only backfill empty fields to avoid overwriting user edits
      const next = buildInitialDetails();
      return {
        policyNumber: prev.policyNumber || next.policyNumber,
        claimNumber: prev.claimNumber || next.claimNumber,
        adjusterName: prev.adjusterName || next.adjusterName,
        adjusterEmail: prev.adjusterEmail || next.adjusterEmail,
        adjusterPhone: prev.adjusterPhone || next.adjusterPhone,
        adjusterFax: prev.adjusterFax || next.adjusterFax,
        preferredContactInstructions: prev.preferredContactInstructions || '',
        adjusterContactChoice: prev.adjusterContactChoice || next.adjusterContactChoice,
      };
    });
    prefilledRef.current = true;
  }, [coverageData, coverageId]);

  const updateFinalizeDetail = useCallback(
    <K extends keyof FinalizeDetails>(key: K, value: FinalizeDetails[K]) => {
      setFinalizeDetails((prev) => ({ ...prev, [key]: value }));
      setHasUnsavedFinalizeChanges(true);
    },
    [],
  );

  const saveFinalizeDetails = useCallback(() => {
    setHasUnsavedFinalizeChanges(false);
    toast.success('Details saved');
  }, []);

  const handleFaxLor = useCallback(
    async (lorId: string, faxNumber: string) => {
      try {
        await sendFaxFromBlob({
          blob_id: lorId,
          recipient_number: faxNumber,
        }).unwrap();
        toast.success('Fax sent successfully');
      } catch (error) {
        toast.error('Failed to send fax');
      }
    },
    [sendFaxFromBlob],
  );

  const handleTransmissionComplete = useCallback((lorId: string) => {
    // eslint-disable-next-line no-console
    console.log(`Fax transmission completed for LoR ${lorId}`);
  }, []);

  if (!matterId || !coverageId) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-zinc-500">Missing matter or coverage ID</p>
      </div>
    );
  }

  const pageBackgroundClass =
    'min-h-screen bg-gradient-to-b from-slate-50 via-white to-white';
  const contentWrapperClass =
    'mx-auto w-full max-w-[2200px] space-y-6 px-8 py-6';

  return (
    <div className={pageBackgroundClass}>
      <div className={contentWrapperClass}>
        {!hideHeader && (
          <div className="w-full">
            <div className="w-full py-4 flex items-center justify-between">
              <h2 className="text-3xl font-serif text-zinc-800 m-0">
                Write a Letter of Representation (Legacy)
              </h2>
              <div className="flex items-center gap-3">
                {isTaskCompleted ? (
                  <div className="flex items-center gap-2 text-sm text-green-700">
                    <Check className="h-4 w-4" />
                    Completed
                  </div>
                ) : (
                  <Button
                    size="lg"
                    className="h-9 px-4"
                    onClick={handleMarkComplete}
                    disabled={isUpdating || !completedStatusId || !sendLorTask}
                  >
                    {isUpdating ? 'Updating…' : 'Mark Complete'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
        <DraftClaimLorStep
          matterId={matterId}
          coverageId={coverageId}
          setActiveFieldKey={setActiveFieldKey}
          finalizeDetails={finalizeDetails}
          updateFinalizeDetail={updateFinalizeDetail}
          hasUnsavedFinalizeChanges={hasUnsavedFinalizeChanges}
          saveFinalizeDetails={saveFinalizeDetails}
          activeFaxTransmissions={activeFaxTransmissions}
          onSendFax={handleFaxLor}
          onTransmissionComplete={handleTransmissionComplete}
          isSendingFax={isSendingFax}
        />
      </div>
    </div>
  );
};
